import { fg } from '@atlaskit/platform-feature-flags/fg';
import { IdentityUserAri } from '@atlassian/ari';

const BITBUCKET_INTEGRATION_ARI = 'ari:cloud:platform::integration/bitbucket';
const GITHUB_INTEGRATION_ARI = 'ari:cloud:platform::integration/github';

/**
 * Note that we can't sort by entity.lastUpdated as this field is missing for some entities (Projects + Goals partially, Pull Requests entirely)
 * If the field is null, Cypher brings those results to the top, which is a random mismash of old relationships (think 2024 PRs)
 *
 * We no longer use entity.lastUpdated because it breaks relay conventions - missing columns return {} instead of {value: null}
 * this invalidates responses in the store immediately. store-and-network / store-or-network then fails to work as expected
 *
 * useHomeAssignedCypherQuery uses rel.createdAt as a fallback if entity (on AGG) lastUpdated fails
 */

const TOWNSQUARE_RELATIONSHIPS = ['atlas_goal_has_owner', 'atlas_project_has_owner'];
const generateTownsquareCypherQuery = (accountId: string): string => {
	const userAri = IdentityUserAri.create({ userId: accountId }).toString();

	// Note that unlike the other queries, we can't filter on project / goal state.
	// The entities don't have the info yet. We're FE filtering out invalid entities instead.
	return (
		`MATCH (user:IdentityUser {ari: '${userAri}'})<-[rel:${TOWNSQUARE_RELATIONSHIPS.join('|')}]-(townsquareEntity) ` +
		`RETURN townsquareEntity, rel.createdAt ` +
		`ORDER BY rel.createdAt DESC`
	);
};

const JIRA_ISSUE_RELATIONSHIPS = ['user_assigned_issue'];
const generateJiraIssueCypherQuery = (accountId: string): string => {
	const userAri = IdentityUserAri.create({ userId: accountId }).toString();

	return (
		`MATCH (user:IdentityUser {ari: '${userAri}'})-[rel:${JIRA_ISSUE_RELATIONSHIPS.join('|')}]->(issue:JiraIssue) ` +
		`WHERE issue.statusCategory <> 'done' ` +
		`RETURN issue, rel.createdAt ` +
		`ORDER BY rel.createdAt DESC`
	);
};

const PR_RELATIONSHIPS = ['user_reviews_pr', 'user_authored_pr'];
const generatePullRequestCypherQuery = (accountId: string): string => {
	const userAri = IdentityUserAri.create({ userId: accountId }).toString();

	// When the gate is on, include GitHub PRs alongside Bitbucket. Otherwise keep
	// Bitbucket PRs exclusively (pending permission to show all third-party PRs).
	const integrationAriClause = fg('townsquare_home_assigned_include_github_items')
		? `WHERE rel_3p.integrationAri IN ['${BITBUCKET_INTEGRATION_ARI}', '${GITHUB_INTEGRATION_ARI}'] `
		: `WHERE rel_3p.integrationAri = '${BITBUCKET_INTEGRATION_ARI}' `;

	const cypherQuery =
		`MATCH (user:IdentityUser {ari: '${userAri}'})-[rel_3p:user_linked_third_party_user]->(thirdPartyUser:IdentityThirdPartyUser) ` +
		integrationAriClause +
		`MATCH (thirdPartyUser)-[rel:${PR_RELATIONSHIPS.join('|')}]->(pr:ExternalPullRequest) ` +
		`WHERE pr.status IN ['OPEN', 'DRAFT'] ` +
		`RETURN pr, rel.createdAt ` +
		`ORDER BY rel.createdAt DESC`;

	return cypherQuery;
};

export const createTownsquareCypherQuery = (accountId: string): string =>
	generateTownsquareCypherQuery(accountId);

export const createJiraIssueCypherQuery = (accountId: string): string =>
	generateJiraIssueCypherQuery(accountId);

export const createPullRequestCypherQuery = (accountId: string): string =>
	generatePullRequestCypherQuery(accountId);
